import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  signal,
  forwardRef,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgpCombobox,
  NgpComboboxButton,
  NgpComboboxDropdown,
  NgpComboboxInput,
  NgpComboboxOption,
  NgpComboboxPortal,
  provideComboboxState,
} from 'ng-primitives/combobox';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';

import { SuiFormControlBase } from '../shared/cva-helper';
import { resolveSuiBoundary } from '../shared/boundary';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '../form-field/form-field-control';

export interface ComboboxOption<T = unknown> {
  label: string;
  value: T;
  disabled?: boolean;
  /**
   * Optional group bucket. When any visible option carries a `group`, the
   * dropdown renders grouped under non-interactive headers;
   * otherwise it renders flat. The consumer populates this
   * (e.g. the `comboboxOptions` pipe's group-key arg) — the component stays
   * group-source-agnostic.
   */
  group?: string;
}

/** A header + its options, in first-seen order. */
interface ComboboxGroup {
  key: string;
  items: ComboboxOption[];
}

@Component({
  selector: 'sui-combobox',
  standalone: true,
  imports: [
    NgpCombobox,
    NgpComboboxInput,
    NgpComboboxButton,
    NgpComboboxPortal,
    NgpComboboxDropdown,
    NgpComboboxOption,
    FaIconComponent,
  ],
  providers: [
    // State hoisted onto the component instead of via hostDirectives — Angular 20 bug:
    // hostDirectives + useFactory(inject()) inside provideComboboxState() causes DI
    // resolution failure at runtime. Same workaround as SuiSelectComponent.
    provideComboboxState(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiComboboxComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiComboboxComponent),
    },
  ],
  templateUrl: './sui-combobox.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-combobox.component.scss',
})
export class SuiComboboxComponent
  extends SuiFormControlBase<unknown>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-combobox-${++SuiComboboxComponent._idCounter}`;
  private _focused = false;

  // The primitive on the wrapper div. ng-primitives deliberately never opens the
  // dropdown on input click (only arrow keys / typing / the chevron), so we reach
  // for its open method to add click-to-open ourselves.
  @ViewChild(NgpCombobox) private readonly combobox?: NgpCombobox;

  readonly faChevronDown = faChevronDown;
  readonly faXmark = faXmark;

  readonly controlType = 'combobox';

  @Input() id = '';
  @Input() placeholder = 'Search...';
  /**
   * Where the dropdown overlay mounts - a DOM element or a CSS selector.
   * Defaults to the nearest `data-sui-popover-boundary` pane (else `body`), so
   * in-dialog comboboxes stack correctly with no wiring - pass this only to
   * override (e.g. `'body'` for a pane that would clip the dropdown).
   */
  @Input() dropdownContainer?: string | HTMLElement;

  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Resolved per CD (no caching) so a portaled/moved combobox stays correct. */
  protected get resolvedDropdownContainer(): string | HTMLElement {
    return (
      this.dropdownContainer ?? resolveSuiBoundary(this.hostEl.nativeElement)
    );
  }
  /** Multi-select: value becomes an array, dropdown stays open, selections render as chips. */
  @Input() multiple = false;
  @Input() clearable = false;
  @Input() loading = false;
  /** Non-editable but not dimmed (distinct from disabled). */
  @Input() readonly = false;
  /** How many chips show before collapsing to "+N more" (multi only). */
  @Input() maxVisibleChips = 2;
  /** Equality for value matching — supply for object-valued options. */
  @Input() compareWith: (a: unknown, b: unknown) => boolean = Object.is;

  /**
   * Server-driven filtering. When true, the local substring filter is skipped —
   * the component renders `[options]` as-is and emits `(searchChange)` so the
   * parent owns the fetch pipeline (debounce + request + update `[options]`).
   */
  @Input() serverFilter = false;
  /** Min query length before searching (server mode); below it, `typeToSearchText` shows. */
  @Input() minTermLength = 0;
  /** Dropdown prompt shown while the query is below `minTermLength` (server mode). */
  @Input() typeToSearchText = 'Start typing…';
  /** Emits the typed query (server mode only) — parent debounces + fetches. */
  @Output() searchChange = new EventEmitter<string>();

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  @Input()
  set options(val: ComboboxOption[]) {
    this._options.set(val ?? []);
    this.cacheSelectedOption();
  }
  private readonly _options = signal<ComboboxOption[]>([]);

  // Last option resolved for the committed value (single-select). Under
  // server-filter the options list is replaced on every query, so the selected
  // value may no longer be present — this keeps its label showable. Plain field
  // (not a signal): it's only ever updated alongside an `_options`/`value` change,
  // both of which `selectedLabel` already tracks, so the computed stays correct.
  private _resolvedOption: ComboboxOption | null = null;
  private cacheSelectedOption(): void {
    if (this.multiple) return;
    const match = this._options().find((o) =>
      this.compareWith(o.value, this.value()),
    );
    if (match) this._resolvedOption = match;
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);
    this.cacheSelectedOption();
  }

  // The text the user is actively typing; separate from the selected label.
  readonly query = signal('');

  // Blocks all interaction for both disabled and readonly; only `disabled` dims (see SCSS).
  get effectiveDisabled(): boolean {
    return this.isDisabled() || this.readonly;
  }

  // The value handed to the primitive — an array in multi mode so the primitive's
  // array logic always has something to spread.
  get comboValue(): unknown {
    return this.multiple ? (this.value() ?? []) : this.value();
  }

  // Server mode: the parent already filtered, so render options as-is.
  // Local mode: case-insensitive substring filter (empty query = show all).
  readonly filteredOptions = computed(() => {
    const opts = this._options();
    if (this.serverFilter) return opts;
    const q = this.query().toLowerCase().trim();
    if (!q) return opts;
    // Guard the label: it's typed `string`, but options are often built from
    // untyped API lists where a value can arrive as a number (or null). Coerce
    // so the filter never throws on a non-string label.
    return opts.filter((o) =>
      String(o.label ?? '')
        .toLowerCase()
        .includes(q),
    );
  });

  // Grouped rendering kicks in only when some visible option declares a group;
  // 16/18 ungrouped configs keep the flat list with zero extra wiring.
  readonly isGrouped = computed(() =>
    this.filteredOptions().some((o) => !!o.group),
  );

  // Buckets the (already-filtered) options by `group`, preserving first-seen
  // order of both groups and items. Empty groups never appear because they hold
  // no filtered items. Ungrouped options collapse under an empty-key bucket
  // (rendered headerless by the template).
  readonly groupedOptions = computed<ComboboxGroup[]>(() => {
    const groups: ComboboxGroup[] = [];
    const byKey = new Map<string, ComboboxGroup>();
    for (const opt of this.filteredOptions()) {
      const key = opt.group ?? '';
      let group = byKey.get(key);
      if (!group) {
        group = { key, items: [] };
        byKey.set(key, group);
        groups.push(group);
      }
      group.items.push(opt);
    }
    return groups;
  });

  // Single-select: the label of the currently-selected option. Prefers the
  // current options; falls back to the last resolved option for this value
  // (server-filter: the committed value may no longer be in the re-queried list).
  readonly selectedLabel = computed(() => {
    if (this.multiple) return '';
    const value = this.value();
    const selected = this._options().find((o) =>
      this.compareWith(o.value, value),
    );
    if (selected) return selected.label;
    const cached = this._resolvedOption;
    if (cached && this.compareWith(cached.value, value)) return cached.label;
    return '';
  });

  // Single-select: render the committed value as a separate, non-editable label
  // over the (empty) search input — shown when there's a value and the user
  // isn't actively searching. Keeping it out of the input is what makes typing/
  // deleting never mutate the value; the value is removed
  // only via the ✕ or Backspace-on-empty-search (clearable — see onInputKeydown).
  readonly showSelectedValue = computed(
    () => !this.multiple && this.query() === '' && this.selectedLabel() !== '',
  );

  // Multi-select: the option objects behind the currently-selected values.
  readonly selectedOptions = computed<ComboboxOption[]>(() => {
    if (!this.multiple) return [];
    const values = (this.value() as unknown[]) ?? [];
    return this._options().filter((o) =>
      values.some((v) => this.compareWith(v, o.value)),
    );
  });

  readonly visibleChips = computed(() =>
    this.selectedOptions().slice(0, this.maxVisibleChips),
  );
  readonly overflowCount = computed(() =>
    Math.max(0, this.selectedOptions().length - this.maxVisibleChips),
  );

  get effectiveId(): string {
    return this.id || this._uid;
  }

  // Placeholder hidden when the selected-value label (single) or chips (multi)
  // are showing, so they don't overlap the placeholder text.
  get inputPlaceholder(): string {
    if (this.showSelectedValue()) return '';
    if (this.multiple && this.selectedOptions().length) return '';
    return this.placeholder;
  }

  // Server mode: show the "type to search" prompt until the query is long enough.
  get showTypeToSearch(): boolean {
    return this.serverFilter && this.query().length < this.minTermLength;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    return this.multiple
      ? !((this.value() as unknown[]) ?? []).length
      : this.value() == null;
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  // Clicking the input opens the dropdown (the chevron already toggles; the
  // primitive itself only opens on arrow/typing). Open-only, not toggle:
  // `openDropdown()` self-guards when already open, so re-clicking to place the
  // text cursor never closes the list. Outside-click dismiss treats the input as
  // inside the trigger, so there's no open/close race.
  onInputClick(): void {
    if (this.effectiveDisabled) return;
    this.combobox?.openDropdown();
  }

  onQueryInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.query.set(term);
    // Server mode: hand the term to the parent once it clears the min length.
    if (this.serverFilter && term.length >= this.minTermLength) {
      this.searchChange.emit(term);
    }
  }

  // Backspace on an empty search clears the current value — single-select only,
  // and only when clearable (the keyboard mirror of the ✕). Non-clearable is
  // protected: the value can't be removed by keystroke. Other keys fall through
  // to the primitive's own handling (arrows / enter / escape).
  onInputKeydown(event: KeyboardEvent): void {
    if (
      event.key === 'Backspace' &&
      !this.multiple &&
      this.query() === '' &&
      this.clearable &&
      !this.empty &&
      !this.effectiveDisabled
    ) {
      event.preventDefault();
      this.setValue(null);
    }
  }

  // Fired by the primitive on select/toggle (single: scalar; multi: full array).
  onValueChange(newValue: unknown): void {
    this.setValue(newValue);
    // Remember the picked option so its label survives a later options refresh.
    this.cacheSelectedOption();
    // Reset the typed query so the next interaction shows the full list / selected label.
    this.query.set('');
  }

  removeChip(optValue: unknown, event: Event): void {
    event.stopPropagation();
    const next = ((this.value() as unknown[]) ?? []).filter(
      (v) => !this.compareWith(v, optValue),
    );
    this.setValue(next);
  }

  clearAll(event: Event): void {
    event.stopPropagation();
    this.setValue(this.multiple ? [] : null);
    this.query.set('');
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
    // Discard any partial search; the selected-value label re-appears (query is
    // empty again) with the committed value intact.
    this.query.set('');
  }
}
